import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type { ViewDetail } from "@utils/viewDefinition/types";
import type { UnavailableItem } from "@utils/viewDefinition/enrichment";

const STORAGE_KEY = "viewsV2.provenanceDismissed";

/** Newest-first eviction bound. Dismissals are a convenience, not a record. */
const MAX_DISMISSED_ENTRIES = 200;

/** key -> epoch ms of the dismissal */
export type ProvenanceDismissals = Record<string, number>;

export interface ViewsV2State {
  detailsById: Record<string, ViewDetail>;
  provenanceDismissed: ProvenanceDismissals;
}

/**
 * Dismissal is keyed on the user, the view, AND a digest of what was unavailable.
 *
 * Keying on `viewId` alone silently suppresses a future notice about a *different*
 * reference: a view dismissed over a lapsed brand would hide the day a filter lapses,
 * and a lost filter changes the population the numbers describe. Entitlement is
 * per-user, so the caller is in the key too -- a shared machine must not inherit
 * someone else's dismissals.
 */
export function buildProvenanceDismissKey(
  userId: number | undefined,
  viewId: string,
  unavailable: UnavailableItem[] | undefined,
): string {
  const signature = (unavailable ?? [])
    .map((item) => `${item.kind}|${item.id}|${item.reason}`)
    .sort()
    .join(",");
  let hash = 5381;
  for (let i = 0; i < signature.length; i += 1) {
    hash = ((hash << 5) + hash + signature.charCodeAt(i)) | 0;
  }
  return `${userId ?? "anon"}:${viewId}:${(hash >>> 0).toString(36)}`;
}

function readProvenanceDismissed(): ProvenanceDismissals {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      (entry): entry is [string, number] => typeof entry[1] === "number",
    );
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
}

export function persistProvenanceDismissed(dismissals: ProvenanceDismissals): void {
  try {
    const entries = Object.entries(dismissals);
    const bounded =
      entries.length > MAX_DISMISSED_ENTRIES
        ? entries.sort((a, b) => b[1] - a[1]).slice(0, MAX_DISMISSED_ENTRIES)
        : entries;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(bounded)));
  } catch {
    // localStorage unavailable or quota exceeded; dismissal remains session-only.
  }
}

function createInitialState(): ViewsV2State {
  return {
    detailsById: {},
    provenanceDismissed: readProvenanceDismissed(),
  };
}

const viewsV2Slice = createSlice({
  name: "viewsV2",
  initialState: createInitialState(),
  reducers: {
    viewDetailLoaded: (state, action: PayloadAction<ViewDetail>) => {
      state.detailsById[action.payload.id] = action.payload;
    },
    viewDetailEvicted: (state, action: PayloadAction<string>) => {
      delete state.detailsById[action.payload];
    },
    provenanceDismissed: (state, action: PayloadAction<{ key: string; at: number }>) => {
      state.provenanceDismissed[action.payload.key] = action.payload.at;
    },
  },
});

export const { viewDetailLoaded, viewDetailEvicted, provenanceDismissed } = viewsV2Slice.actions;

export const selectViewDetail = (state: RootState, viewId: string): ViewDetail | undefined =>
  state.viewsV2.detailsById[viewId];

export const selectIsProvenanceDismissed = (state: RootState, key: string): boolean =>
  key in state.viewsV2.provenanceDismissed;

export default viewsV2Slice.reducer;
